import type { InfiniteData, Query, QueryKey } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import type { HistoryResponse } from '~/domains/actions/queries/useHistory'
import type { ApplicantHistoryEvent } from '../types'
import { getEventChronoDate } from '../utils/eventChronoDate'

type HistoryData = InfiniteData<HistoryResponse>

export type OptimisticEventContext = {
  applicantId: number
  eventId: number
  previousData: [QueryKey, HistoryData | undefined][]
}

const historyQueryKey = (applicantId: number): QueryKey => [
  'applicant',
  applicantId,
  'history'
]

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

// History queries are keyed ['applicant', id, 'history', types[], includeCampaigns]:
// an event only belongs in queries whose type filter is empty or includes it.
const acceptsEventType =
  (eventType: string) =>
  (query: Query): boolean => {
    const types = query.queryKey[3]
    return (
      !isStringArray(types) || types.length === 0 || types.includes(eventType)
    )
  }

/**
 * Inserts an event into the history array in chronological order (newest first)
 * Uses getEventChronoDate to determine the proper position
 */
const insertEventChronologically = (
  events: ApplicantHistoryEvent[],
  newEvent: ApplicantHistoryEvent
): ApplicantHistoryEvent[] => {
  const newEventDate = new Date(getEventChronoDate(newEvent))

  // Find the correct position to insert the event
  const insertIndex = events.findIndex((event) => {
    const eventDate = new Date(getEventChronoDate(event))
    return newEventDate > eventDate
  })

  // If no position found, add to the top of the list (newest)
  if (insertIndex === -1) {
    return [newEvent, ...events]
  }

  // Insert at the correct position
  return [
    ...events.slice(0, insertIndex),
    newEvent,
    ...events.slice(insertIndex)
  ]
}

/**
 * Finds the correct page index to insert an event based on its chronological date
 */
const findInsertPageIndex = (
  pages: HistoryResponse[],
  eventDate: Date
): number => {
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex]
    if (!page || page['hydra:member'].length === 0) {
      continue
    }

    const lastEventInPage =
      page['hydra:member'][page['hydra:member'].length - 1]
    if (!lastEventInPage) {
      continue
    }

    if (eventDate >= new Date(getEventChronoDate(lastEventInPage))) {
      return pageIndex
    }
  }

  return pages.length - 1
}

/**
 * Inserts an event into the infinite query data structure
 * The event is inserted into the appropriate page based on its chronological position
 */
const insertEventIntoInfiniteData = (
  infiniteData: InfiniteData<HistoryResponse>,
  newEvent: ApplicantHistoryEvent
): InfiniteData<HistoryResponse> => {
  const updatedPages = [...infiniteData.pages]

  if (updatedPages.length === 0) {
    // If no pages exist, create the first page
    return {
      pages: [
        {
          'hydra:member': [newEvent],
          'hydra:totalItems': 1
        }
      ],
      pageParams: infiniteData.pageParams
    }
  }

  const newEventDate = new Date(getEventChronoDate(newEvent))
  const insertPageIndex = findInsertPageIndex(updatedPages, newEventDate)

  // Insert the event in the determined page
  const targetPage = updatedPages[insertPageIndex]
  if (!targetPage) {
    throw new Error('Target page should exist at this point')
  }

  const updatedTargetPage = {
    ...targetPage,
    'hydra:member': insertEventChronologically(
      targetPage['hydra:member'],
      newEvent
    ),
    'hydra:totalItems': targetPage['hydra:totalItems'] + 1
  }

  updatedPages[insertPageIndex] = updatedTargetPage

  // Update total items in all pages to maintain consistency
  const updatedPagesWithTotalItems = updatedPages.map((page) => ({
    ...page,
    'hydra:totalItems': updatedTargetPage['hydra:totalItems']
  }))

  return {
    pages: updatedPagesWithTotalItems,
    pageParams: infiniteData.pageParams
  }
}

export const useAddOptimisticHistoryEvent = () => {
  const queryClient = useQueryClient()

  return (
    applicantId: number,
    event: ApplicantHistoryEvent
  ): OptimisticEventContext => {
    const filters = {
      queryKey: historyQueryKey(applicantId),
      predicate: acceptsEventType(event.type)
    }
    const previousData = queryClient.getQueriesData<HistoryData>(filters)

    queryClient.setQueriesData<HistoryData>(filters, (oldData) => {
      if (!oldData) {
        return {
          pages: [
            {
              'hydra:member': [event],
              'hydra:totalItems': 1
            }
          ],
          pageParams: [1]
        }
      }
      return insertEventIntoInfiniteData(oldData, event)
    })

    return {
      applicantId,
      eventId: event.id,
      previousData
    }
  }
}

export const useAddOptimisticHistoryEvents = () => {
  const addOptimisticHistoryEvent = useAddOptimisticHistoryEvent()

  return (
    applicantIds: number[],
    eventGenerator: (applicantIds: number[]) => ApplicantHistoryEvent
  ): OptimisticEventContext[] => {
    // We need to store the optimistic events in an array to be able to rollback them (one stack per applicant)
    const optimisticEvents: OptimisticEventContext[] = []

    for (const applicantId of applicantIds) {
      const optimisticEvent = eventGenerator([applicantId])
      const context = addOptimisticHistoryEvent(applicantId, optimisticEvent)
      optimisticEvents.push(context)
    }

    return optimisticEvents
  }
}

/**
 * Updates an event in the infinite query data structure
 * Handles reordering if the chronological date changes
 */
const updateEventInInfiniteData = (
  infiniteData: InfiniteData<HistoryResponse>,
  eventId: number,
  updateFn: (event: ApplicantHistoryEvent) => ApplicantHistoryEvent
): InfiniteData<HistoryResponse> => {
  let updatedEvent: ApplicantHistoryEvent | undefined
  let previousEvent: ApplicantHistoryEvent | undefined
  let eventPageIndex = -1

  // Find the event across all pages
  const updatedPages = infiniteData.pages.map((page, pageIndex) => {
    const eventIndex = page['hydra:member'].findIndex(
      (event) => event.id === eventId
    )

    if (eventIndex !== -1) {
      eventPageIndex = pageIndex
      previousEvent = page['hydra:member'][eventIndex]
      if (previousEvent) {
        updatedEvent = updateFn(previousEvent)
      }

      // Remove the event from its current position
      const updatedMembers = [...page['hydra:member']]
      updatedMembers.splice(eventIndex, 1)

      return {
        ...page,
        'hydra:member': updatedMembers
      }
    }

    return page
  })

  if (!updatedEvent || !previousEvent || eventPageIndex === -1) {
    return infiniteData
  }

  const oldChronoDate = getEventChronoDate(previousEvent)
  const newChronoDate = getEventChronoDate(updatedEvent)

  // If chronological date didn't change, put it back in the same page at the same relative position
  if (oldChronoDate === newChronoDate) {
    const targetPage = updatedPages[eventPageIndex]
    if (!targetPage) {
      return infiniteData
    }

    const updatedMembers = [...targetPage['hydra:member']]

    // Find where to reinsert based on chronological order
    const insertIndex = updatedMembers.findIndex((event) => {
      const eventDate = new Date(getEventChronoDate(event))
      const updatedEventDate = new Date(newChronoDate)
      return updatedEventDate > eventDate
    })

    if (insertIndex === -1) {
      updatedMembers.push(updatedEvent)
    } else {
      updatedMembers.splice(insertIndex, 0, updatedEvent)
    }

    updatedPages[eventPageIndex] = {
      ...targetPage,
      'hydra:member': updatedMembers,
      'hydra:totalItems': targetPage['hydra:totalItems']
    }
  } else {
    // If chronological date changed, we need to reinsert in the correct chronological position
    const updatedEventDate = new Date(newChronoDate)
    const insertPageIndex = findInsertPageIndex(updatedPages, updatedEventDate)

    const targetPage = updatedPages[insertPageIndex]
    if (!targetPage) {
      return infiniteData
    }

    updatedPages[insertPageIndex] = {
      ...targetPage,
      'hydra:member': insertEventChronologically(
        targetPage['hydra:member'],
        updatedEvent
      ),
      'hydra:totalItems': targetPage['hydra:totalItems']
    }
  }

  return {
    pages: updatedPages,
    pageParams: infiniteData.pageParams
  }
}

export const useUpdateOptimisticHistoryEvent = () => {
  const queryClient = useQueryClient()

  return (
    applicantId: number,
    eventId: number,
    updateFn: (event: ApplicantHistoryEvent) => ApplicantHistoryEvent
  ): OptimisticEventContext => {
    const filters = { queryKey: historyQueryKey(applicantId) }
    const previousData = queryClient.getQueriesData<HistoryData>(filters)

    queryClient.setQueriesData<HistoryData>(filters, (oldData) => {
      if (!oldData) return oldData
      return updateEventInInfiniteData(oldData, eventId, updateFn)
    })

    return {
      applicantId,
      eventId,
      previousData
    }
  }
}

export const useUpdateOptimisticEventsWithRealIds = () => {
  const updateOptimisticHistoryEvent = useUpdateOptimisticHistoryEvent()

  return (contexts: OptimisticEventContext[], realIds: number[]): void => {
    for (let i = 0; i < contexts.length; i++) {
      const context = contexts[i]
      const realId = realIds[i]

      if (context && realId) {
        updateOptimisticHistoryEvent(
          context.applicantId,
          context.eventId,
          (event) => ({
            ...event,
            id: realId
          })
        )
      }
    }
  }
}

export const useRollbackOptimisticHistoryEvents = () => {
  const queryClient = useQueryClient()

  return (contexts: OptimisticEventContext[]): void => {
    for (const { previousData } of contexts) {
      for (const [queryKey, data] of previousData) {
        queryClient.setQueryData<HistoryData>(queryKey, data)
      }
    }
  }
}
